import React, { useState, useCallback, useMemo, useEffect } from "react";
import { CheckCircle, XCircle, User, Calendar, Clock, Filter, FileText } from "lucide-react";
import { ApiService } from "../../../services/api";
import { AppointmentItem } from "../../../types/appointment";
import checkPermission from "../../../utils/permission";
import DynamicList from "../../../components/dynamic/List";
import { useModal } from "../../../hooks/useModal";
import AppointmentStatusFilter, { AppointmentStatusFilterType } from "./AppointmentStatusFilter";
import Modal from "../../../components/ui/Modal";
import { LIST_TOOLBAR_BTN, LIST_TOOLBAR_ICON, LIST_TOOLBAR_ROW } from "../../../constants/listToolbarButtons";

type AppointmentRowProps = {
  apt: AppointmentItem;
  canApprove: boolean;
  onApprove: (id: string, apt: AppointmentItem) => void;
  onReject: (id: string) => void;
  onSaved: () => void;
};

function AppointmentRow({ apt, canApprove, onApprove, onReject, onSaved }: AppointmentRowProps) {
  const { showAlert, showConfirm } = useModal();
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteValue, setNoteValue] = useState<string>(apt.description || "");
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  const isAttendanceEligible = ["APPROVED", "COMPLETED"].includes(apt.status) && new Date(apt.endDate) < new Date();

  const saveNote = async () => {
    try {
      await ApiService.updateAppointment(apt.id, {
        description: noteValue || "",
      });
      setNoteOpen(false);
      onSaved();
      await showAlert({
        title: "Not Kaydedildi",
        message: "Randevu notu başarıyla güncellendi.",
        type: "success",
      });
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error?.message || "Not kaydedilirken bir hata oluştu.",
        type: "error",
      });
    }
  };

  const cancelApproved = async () => {
    const confirmed = await showConfirm({
      title: "Randevu İptali",
      message: "Bu randevuyu iptal etmek istediğinizden emin misiniz?",
      confirmText: "Evet, İptal Et",
      cancelText: "Vazgeç",
      confirmButtonVariant: "danger",
    });

    if (!confirmed) return;

    try {
      setLoadingCancel(true);
      await ApiService.updateAppointment(apt.id, { status: "CANCELLED" });
      onSaved();
      await showAlert({
        title: "Randevu İptal Edildi",
        message: "Randevu başarıyla iptal edildi.",
        type: "success",
      });
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error?.message || "Randevu iptali sırasında bir hata oluştu.",
        type: "error",
      });
    } finally {
      setLoadingCancel(false);
    }
  };

  const handleMarkAttendance = async (status: "ATTENDED" | "NO_SHOW" | "UNMARKED") => {
    const statusLabels = {
      ATTENDED: "Katıldı",
      NO_SHOW: "Katılmadı",
      UNMARKED: "İşaretlenmemiş",
    };

    try {
      setLoadingAttendance(true);
      await ApiService.markAttendance(apt.id, status);
      onSaved();
      await showAlert({
        title: "Katılım Durumu Güncellendi",
        message: `Katılım durumu "${statusLabels[status]}" olarak işaretlendi.`,
        type: "success",
      });
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error?.message || "Katılım durumu güncellenirken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setLoadingAttendance(false);
    }
  };

  return (
    <div
      key={apt.id}
      className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 hover:bg-gray-100 transition-all duration-200 overflow-hidden max-w-full">
      <div className="flex flex-col md:flex-row md:items-start gap-3 sm:gap-4">
        <div className="flex-1 space-y-2 min-w-0 overflow-hidden">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
              {apt.user?.media?.url ? (
                <img src={apt.user.media.url} alt={`${apt.user.firstName} ${apt.user.lastName}`} className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-sm font-medium">
                  {(apt.user?.firstName || "").charAt(0)}
                  {(apt.user?.lastName || "").charAt(0)}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <h3 className="text-gray-900 font-semibold text-sm sm:text-base truncate overflow-hidden">
                {`${apt.user?.firstName ?? ""} ${apt.user?.lastName ?? ""}`.trim() || apt.title || "Randevu"}
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm truncate overflow-hidden">{apt.user?.email}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600">
            <span className="flex items-center space-x-1">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>{new Date(apt.startDate).toLocaleDateString()}</span>
            </span>
            <span className="flex items-center space-x-1">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>
                {new Date(apt.startDate).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                {" - "}
                {new Date(apt.endDate).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </span>
          </div>
          {apt.description && (
            <p className="text-gray-600 text-xs sm:text-sm italic break-all line-clamp-2 overflow-hidden">"{apt.description}"</p>
          )}
          {isAttendanceEligible && (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="text-gray-700 text-xs sm:text-sm font-medium">Katılım Durumu:</span>
              <button
                onClick={() => handleMarkAttendance("ATTENDED")}
                disabled={loadingAttendance}
                className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                  apt.attendanceStatus === "ATTENDED"
                    ? "bg-green-100 text-green-800 border border-green-200"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-green-50"
                } disabled:opacity-60 disabled:cursor-not-allowed`}>
                Katıldı
              </button>
              <button
                onClick={() => handleMarkAttendance("NO_SHOW")}
                disabled={loadingAttendance}
                className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                  apt.attendanceStatus === "NO_SHOW"
                    ? "bg-red-100 text-red-800 border border-red-200"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-red-50"
                } disabled:opacity-60 disabled:cursor-not-allowed`}>
                Katılmadı
              </button>
              <button
                onClick={() => handleMarkAttendance("UNMARKED")}
                disabled={loadingAttendance}
                className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                  apt.attendanceStatus === "UNMARKED" || !apt.attendanceStatus
                    ? "bg-gray-100 text-gray-800 border border-gray-300"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                } disabled:opacity-60 disabled:cursor-not-allowed`}>
                İşaretlenmedi
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
          {apt.status === "PENDING" && canApprove ? (
            <>
              <button
                onClick={() => onApprove(apt.id, apt)}
                className="flex items-center space-x-1 sm:space-x-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 shadow-lg text-xs sm:text-sm">
                <CheckCircle size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span className="font-medium">Onayla</span>
              </button>
              <button
                onClick={() => onReject(apt.id)}
                className="flex items-center space-x-1 sm:space-x-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all duration-200 shadow-lg text-xs sm:text-sm">
                <XCircle size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span className="font-medium">Reddet</span>
              </button>
            </>
          ) : (
            <>
              <div
                className={`flex items-center space-x-1 sm:space-x-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-medium text-xs sm:text-sm ${
                  apt.status === "APPROVED"
                    ? "bg-green-100 text-green-800 border border-green-200"
                    : apt.status === "CANCELLED"
                    ? "bg-gray-100 text-gray-800 border border-gray-300"
                    : "bg-red-100 text-red-800 border border-red-200"
                }`}>
                {apt.status === "APPROVED" ? (
                  <>
                    <CheckCircle size={16} className="sm:w-[18px] sm:h-[18px]" />
                    <span>Onaylandı</span>
                  </>
                ) : apt.status === "CANCELLED" ? (
                  <>
                    <XCircle size={16} className="sm:w-[18px] sm:h-[18px]" />
                    <span>İptal Edildi</span>
                  </>
                ) : (
                  <>
                    <XCircle size={16} className="sm:w-[18px] sm:h-[18px]" />
                    <span>Reddedildi</span>
                  </>
                )}
              </div>
              {apt.status === "APPROVED" && new Date(apt.endDate) > new Date() && (
                <button
                  onClick={cancelApproved}
                  disabled={loadingCancel}
                  className="flex items-center space-x-1 sm:space-x-2 px-2 py-1.5 sm:px-3 sm:py-2 border border-red-300 text-red-700 rounded-lg text-xs sm:text-sm bg-white hover:bg-red-50 transition-colors disabled:opacity-60">
                  <XCircle size={16} className="sm:w-[18px] sm:h-[18px]" />
                  <span>{loadingCancel ? "İptal ediliyor..." : "İptal Et"}</span>
                </button>
              )}
              <button
                onClick={() => setNoteOpen(true)}
                className="flex items-center space-x-1 sm:space-x-2 px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white hover:bg-gray-50 transition-colors"
                title={apt.description ? "Notu Gör / Düzenle" : "Not Ekle"}>
                <FileText size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span className="hidden sm:inline">Not</span>
                <span className="sm:hidden">Not</span>
              </button>
            </>
          )}
        </div>
      </div>

      <Modal isOpen={noteOpen} onClose={() => setNoteOpen(false)} title="Randevu Notu" size="md">
        <div className="space-y-4">
          <div>
            <textarea
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value.slice(0, 255))}
              placeholder="Bu randevu hakkında not giriniz..."
              rows={6}
              maxLength={255}
              className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
            />
            <div className="flex justify-between items-center mt-2">
              <p className="text-xs text-gray-500">Maksimum 255 karakter</p>
              <p className={`text-xs ${noteValue.length >= 255 ? "text-red-600 font-medium" : "text-gray-500"}`}>{noteValue.length}/255</p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setNoteOpen(false)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all duration-200">
              İptal
            </button>
            <button
              onClick={saveNote}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200">
              Kaydet
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default function ProviderViewAppointments() {
  const { showAlert } = useModal();
  const [filterStatus, setFilterStatus] = useState<AppointmentStatusFilterType>("all");
  const [refreshKey, setRefreshKey] = useState(0);
  const [filterOpen, setFilterOpen] = useState(true);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const canApprove = checkPermission("appointment_update");

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        const isMobile = window.matchMedia && window.matchMedia("(max-width: 640px)").matches;
        if (isMobile) setFilterOpen(false);
      }
    } catch (_) {}
  }, []);

  const fetchAppointments = useCallback(
    async (page: number, searchTerm?: string) => {
      const res = await ApiService.getMyProviderAppointments({
        sortBy: "startDate",
        sortOrder: "DESC",
        limit: 20,
        page,
        search: searchTerm || undefined,
        status: filterStatus === "all" ? undefined : filterStatus,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      const anyRes: any = res as any;
      const meta = anyRes?.meta || {};
      const pageNum = meta.page ?? anyRes.page ?? page ?? 1;
      const limitNum = meta.limit ?? anyRes.limit ?? 20;
      const totalItemsNum = meta.totalItems ?? anyRes.totalItems ?? (Array.isArray(anyRes?.data) ? anyRes.data.length : 0);
      const totalPagesNum = meta.totalPages ?? anyRes.totalPages ?? Math.max(1, Math.ceil((totalItemsNum || 0) / (limitNum || 1)));

      return {
        data: anyRes?.data ?? [],
        meta: {
          page: pageNum,
          totalPages: totalPagesNum,
          totalItems: totalItemsNum,
          limit: limitNum,
        },
      };
    },
    [filterStatus, startDate, endDate]
  );

  const handleDateChange = useCallback((newStartDate: string | null, newEndDate: string | null) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  }, []);

  const renderFilter = useCallback(() => {
    return (
      <AppointmentStatusFilter
        startDate={startDate}
        endDate={endDate}
        onDateChange={handleDateChange}
        selectedStatus={filterStatus}
        onStatusChange={setFilterStatus}
      />
    );
  }, [startDate, endDate, filterStatus, handleDateChange]);

  const appointmentActions = useMemo(
    () => (
      <div className={LIST_TOOLBAR_ROW}>
        <button
          type="button"
          onClick={() => setFilterOpen((prev) => !prev)}
          className={`${LIST_TOOLBAR_BTN} ${filterOpen
              ? "bg-blue-600 text-white hover:bg-blue-700 border-blue-600"
              : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          }`}
          title="Filtrele"
          aria-pressed={filterOpen}>
          <Filter className={LIST_TOOLBAR_ICON} />
          <span>Filtrele</span>
        </button>
      </div>
    ),
    [filterOpen]
  );

  const handleApprove = async (appointmentId: string, appointment: AppointmentItem) => {
    try {
      const updated = await ApiService.approveAppointment(appointmentId);

      const userName = `${updated.user?.firstName ?? ""} ${updated.user?.lastName ?? ""}`.trim();
      const startDate = new Date(updated.startDate).toLocaleDateString();
      const startTime = new Date(updated.startDate).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      const endTime = new Date(updated.endDate).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });

      await showAlert({
        title: "Randevu Onaylandı!",
        message: `${userName} adlı kullanıcının ${startDate} tarihli, ${startTime} - ${endTime} saatleri arasındaki randevusu onaylandı.`,
        type: "success",
      });

      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error?.message || "Randevu onaylanırken bir hata oluştu.",
        type: "error",
      });
    }
  };

  const handleReject = async (appointmentId: string) => {
    try {
      await ApiService.rejectAppointment(appointmentId);

      await showAlert({
        title: "Randevu Reddedildi",
        message: "Randevu başarıyla reddedildi.",
        type: "success",
      });

      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error?.message || "Randevu reddedilirken bir hata oluştu.",
        type: "error",
      });
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col h-full">
      <DynamicList
        embedded
        key={`${filterStatus}-${refreshKey}-${startDate}-${endDate}`}
        type="list"
        header={false}
        title="Randevu Talepleri"
        actions={appointmentActions}
        isSearch={true}
        isPagination={true}
        path=""
        fetcher={fetchAppointments}
        emptyMessage="Gösterilecek randevu bulunmuyor"
        item={(row) => (
          <AppointmentRow
            key={(row as AppointmentItem).id}
            apt={row as AppointmentItem}
            canApprove={canApprove}
            onApprove={handleApprove}
            onReject={handleReject}
            onSaved={() => setRefreshKey((prev) => prev + 1)}
          />
        )}
        leftArea={filterOpen ? renderFilter : undefined}
      />
    </div>
  );
}
